/**
 * Componente SEOHead
 * 
 * Componente reutilizável para meta tags e SEO
 * Inclui: Meta tags básicas, Open Graph, Twitter Cards, Canonical URLs e Structured Data
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { generateMetaTags, generateOrganizationStructuredData } from '../../utils/seo';

const SEOHead = ({
  title,
  description,
  image,
  url,
  type = 'website',
  keywords = '',
  noindex = false,
  structuredData = null,
  locale = 'pt_BR',
}) => {
  const metaTags = generateMetaTags(title, description, image, url, type);
  const orgData = generateOrganizationStructuredData();

  return (
    <Helmet>
      {/* Meta Tags Básicas */}
      <html lang="pt-BR" />
      <title>{metaTags.title}</title>
      <meta name="description" content={metaTags.description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Canonical URL - Evita conteúdo duplicado */}
      <link rel="canonical" href={metaTags.canonical} />
      
      {/* Open Graph - Facebook e outras redes sociais */}
      <meta property="og:title" content={metaTags.openGraph.title} />
      <meta property="og:description" content={metaTags.openGraph.description} />
      <meta property="og:image" content={metaTags.openGraph.image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={metaTags.openGraph.title} />
      <meta property="og:url" content={metaTags.openGraph.url} />
      <meta property="og:type" content={metaTags.openGraph.type} />
      <meta property="og:site_name" content={metaTags.openGraph.siteName} />
      <meta property="og:locale" content={metaTags.openGraph.locale || locale} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content={metaTags.twitter.card} />
      <meta name="twitter:title" content={metaTags.twitter.title} />
      <meta name="twitter:description" content={metaTags.twitter.description} />
      <meta name="twitter:image" content={metaTags.twitter.image} />
      <meta name="twitter:image:alt" content={metaTags.twitter.title} />
      {metaTags.twitter.site && <meta name="twitter:site" content={metaTags.twitter.site} />}
      {metaTags.twitter.creator && <meta name="twitter:creator" content={metaTags.twitter.creator} />}
      
      {/* Pinterest Rich Pins */}
      <meta name="pinterest" content="nohover" />
      <meta name="pinterest" content="nopin" description="true" />
      {image && <link rel="image_src" href={metaTags.openGraph.image} />}
      
      {/* Structured Data - Organization (sempre presente) */}
      <script type="application/ld+json">
        {JSON.stringify(orgData)}
      </script>
      
      {/* Structured Data - Custom (produto, coleção, etc.) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;

